/**
 * Fold a line's manufacturing job events into its audit history.
 *
 * The line-level timeline and the per-sub-job timeline answer the same
 * question from two sides: the line log says the promise date moved, the job
 * log says who moved it and on which sub-job. Kept apart, you have to read
 * both and interleave them by eye. Merged, the order of events tells the
 * story on its own.
 *
 * Job events are mapped onto ChangelogEntry rather than given their own
 * renderer so they inherit the timeline the rest of the audit history already
 * uses — one visual language, not two.
 */
import type { ChangelogEntry } from '../common/Changelog';
import type { Job } from '../../lib/api';

/**
 * Synthetic ids for merged job rows.
 *
 * ChangelogEntry.id keys the React list and is shared with real change-log
 * rows, so job ids are offset far past any real primary key to guarantee no
 * collision.
 */
const JOB_ENTRY_ID_OFFSET = 1_000_000_000;

/** Short form of an ERP job number: "0000097141_1" reads as "97141_1". */
function shortJobNumber(jobNumber: string): string {
  return jobNumber.replace(/^0+/, '');
}

/**
 * Convert one line's jobs into audit entries.
 *
 * @param jobs - jobs imported for the line, each carrying its ERP events
 * @returns entries shaped for the shared Changelog timeline
 */
export function jobEventsToChangelogEntries(jobs: Job[]): ChangelogEntry[] {
  const entries: ChangelogEntry[] = [];

  for (const job of jobs) {
    for (const event of job.events ?? []) {
      entries.push({
        id: JOB_ENTRY_ID_OFFSET + event.id,
        change_date: event.occurred_at,
        // The ERP names the person on every amendment, so the audit row can
        // credit them rather than the import that carried it across.
        changed_by: event.user_name || 'ERP',
        changed_by_company: '',
        field_changed: 'job_event',
        old_value: null,
        // The sub-job is the subject of the row: without it a merged timeline
        // cannot say which of a split's halves moved.
        new_value: `${shortJobNumber(job.job_number)} — ${event.action_label}`,
        reason: event.note || undefined,
        event_type: 'job',
      });
    }
  }

  return entries;
}

/**
 * Line change-log rows the ERP job events restate.
 *
 * DNA writes a line-level progress row ("Job 0000097377_5 Qty4 was ready for
 * dispatch.") for the same fact it reports as a job event. Merging both put
 * every dispatch and start on the timeline twice, one second apart, which is
 * what made the history look shuffled. The job event wins: it names the
 * person and the sub-job, the progress row names neither.
 *
 * Progress rows that carry no job number are line milestones ("Order was
 * received.", "Contract Review was completed.") with no job-event twin, so
 * they must survive.
 */
const JOB_PROGRESS_PATTERN = /\bJob\s+(\S+?)[\s.,]/i;

/**
 * Drop a progress row only when the job it names was actually imported.
 *
 * A partially-imported line (some sub-jobs through, others not) would
 * otherwise lose the only record it has of the missing sub-job: the progress
 * row goes and no job event arrives to replace it.
 */
function makeJobProgressFilter(jobs: Job[]): (entry: ChangelogEntry) => boolean {
  const imported = new Set(jobs.map(j => j.job_number));
  return (entry) => {
    if (entry.field_changed !== 'progress') return false;
    const named = JOB_PROGRESS_PATTERN.exec(entry.new_value || '')?.[1];
    return !!named && imported.has(named);
  };
}

/**
 * Identity of a row as a reader sees it — same instant, same words.
 *
 * Bucketed to the minute because that is the precision the timeline prints,
 * and because the duplicates this exists to catch are written microseconds
 * apart (one per line an ASN covers), so an exact-timestamp key would never
 * match them.
 */
function rowKey(entry: ChangelogEntry): string {
  const minute = Math.floor(new Date(entry.change_date).getTime() / 60_000);
  return [
    String(minute),
    entry.field_changed,
    entry.event_type ?? '',
    entry.old_value ?? '',
    entry.new_value ?? '',
  ].join('\u0000');
}

/**
 * Collapse rows that are indistinguishable to a reader.
 *
 * One ASN event writes a change-log row per line it covers, so a five-line
 * ASN puts five identical "completed → shipped" rows at the same second on
 * every one of those lines. They are kept as one row tagged with the count
 * rather than dropped silently — this is an audit trail, and how many rows
 * the ERP wrote is itself a fact.
 */
function collapseDuplicates(entries: ChangelogEntry[]): ChangelogEntry[] {
  const byKey = new Map<string, { entry: ChangelogEntry; count: number }>();
  for (const entry of entries) {
    const key = rowKey(entry);
    const existing = byKey.get(key);
    if (existing) existing.count += 1;
    else byKey.set(key, { entry, count: 1 });
  }
  return Array.from(byKey.values()).map(({ entry, count }) =>
    count > 1 ? { ...entry, new_value: `${entry.new_value ?? ''} (\u00d7${count})` } : entry,
  );
}

/**
 * Merge job events into the line's change log, newest first.
 *
 * Ties are broken by id so that events sharing a timestamp — routine, since
 * the ERP stamps a whole batch to the same second — hold a stable order
 * instead of reshuffling between renders.
 *
 * @param changelogs - the line's own audit rows
 * @param jobs - jobs imported for the line, or null before they load
 * @returns one deduplicated timeline ordered by date, newest first
 */
export function mergeAuditHistory(
  changelogs: ChangelogEntry[],
  jobs: Job[] | null,
): ChangelogEntry[] {
  const hasJobs = !!jobs && jobs.length > 0;
  const isRestated = hasJobs ? makeJobProgressFilter(jobs) : () => false;
  const base = changelogs.filter(entry => !isRestated(entry));
  const merged = hasJobs ? [...base, ...jobEventsToChangelogEntries(jobs)] : [...base];

  return collapseDuplicates(merged).sort((a, b) => {
    const byDate = new Date(b.change_date).getTime() - new Date(a.change_date).getTime();
    return byDate !== 0 ? byDate : b.id - a.id;
  });
}
